import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Stethoscope, ShieldCheck } from "lucide-react";

const PROFESSIONS = [
  "medico", "odontoiatra", "fisioterapista", "osteopata",
  "psicologo", "dermatologo", "logopedista", "podologo",
  "farmacista", "veterinario", "biologo", "infermiere", "altro",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    profession: "medico", order_number: "", order_country: "IT", order_city: "",
  });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (form.order_number.trim().length < 3) {
      toast.error(t("register.orderRequired"));
      return;
    }
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, {
        profession: form.profession, order_number: form.order_number.trim(),
        order_country: form.order_country, order_city: form.order_city,
      });
      toast.success(t("register.created"));
      navigate("/");
    } catch (e2) {
      toast.error(apiError(e2));
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6 py-12 relative">
      <div className="absolute top-6 right-6 flex items-center gap-3">
        <LanguageSwitcher />
        <Link to="/login" className="text-sm text-stone-600 hover:text-stone-900 underline underline-offset-4" data-testid="register-to-login">
          {t("register.signIn")}
        </Link>
      </div>
      <div className="max-w-lg w-full fade-in-up">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#8FA08B]/15 text-[#5F7059] text-[11px] uppercase tracking-widest mb-4">
          <Stethoscope className="w-3 h-3" /> {t("register.eyebrow")}
        </div>
        <h2 className="font-heading text-4xl font-light mb-2">{t("register.title")}</h2>
        <p className="text-sm text-stone-500 mb-8">{t("register.subtitle")}</p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <Field label={t("register.name")} testId="register-name" required
            value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label={t("login.email")} testId="register-email" type="email" required
            value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label={t("login.password")} testId="register-password" type="password" required minLength={6}
            value={form.password} onChange={(v) => setForm({ ...form, password: v })} />

          <div className="pt-4 border-t border-stone-200 mt-6">
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-[#B07D4F]">
              <ShieldCheck className="w-3.5 h-3.5" /> {t("register.orderSection")}
            </div>
            <p className="text-xs text-stone-500 mb-4">{t("register.orderSectionDesc")}</p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{t("register.profession")} *</label>
            <select value={form.profession} required data-testid="register-profession"
              onChange={(e) => setForm({ ...form, profession: e.target.value })}
              className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373]">
              {PROFESSIONS.map((p) => <option key={p} value={p}>{t(`register.professions.${p}`)}</option>)}
            </select>
          </div>

          <Field label={t("register.orderNumber") + " *"} testId="register-order" required minLength={3}
            value={form.order_number} onChange={(v) => setForm({ ...form, order_number: v })}
            placeholder="12345" />

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("register.orderCountry")} testId="register-country"
              value={form.order_country} onChange={(v) => setForm({ ...form, order_country: v })}
              placeholder="IT / ES" />
            <Field label={t("register.orderCity")} testId="register-city"
              value={form.order_city} onChange={(v) => setForm({ ...form, order_city: v })} />
          </div>

          <button disabled={loading} data-testid="register-submit"
            className="w-full py-3 mt-4 rounded-full bg-[#1C1917] text-white hover:bg-stone-800 transition-colors disabled:opacity-60">
            {loading ? t("register.submitting") : t("register.submit")}
          </button>
        </form>
        <p className="text-sm text-stone-500 mt-6 text-center">
          {t("register.haveAccount")} <Link to="/login" className="text-stone-900 underline underline-offset-4">{t("register.signIn")}</Link>
        </p>
        <p className="text-xs text-stone-400 mt-6 text-center">
          {t("register.orderNote")}
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, minLength, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2 block">{label}</label>
      <input required={required} type={type} minLength={minLength} placeholder={placeholder}
        value={value} data-testid={testId} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl focus:outline-none focus:border-[#D4A373] focus:ring-1 focus:ring-[#D4A373]" />
    </div>
  );
}
